import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './../axiosInstance.js';
import { Button, TextInput, Textarea, Select, NumberInput, Title, Paper, Stack, Text, TagsInput } from '@mantine/core';

const categoryOptions = [
  { value: '0', label: 'Unknown' },
  { value: '1', label: 'Movie' },
  { value: '2', label: 'Video Game' },
  { value: '3', label: 'Book' },
  { value: '4', label: 'YouTube Channel' },
];

const subCategoryOptions = [
  { value: '', label: 'None' },
  { value: 'movie', label: 'Movie' },
  { value: 'series', label: 'Series' },
  { value: 'anime', label: 'Anime' },
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'documentary', label: 'Documentary' },
];

const CreateArt = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    ruName: '',
    ruDescription: '',
    wikiArticleLink: '',
    wikiArticleID: '',
    category: '1',
    thumbnailUrl: '',
    ruWikiArticleLink: '',
    author: '',
    publisher: '',
    otherPlatformsLinks: [],
    year: null,
    subCategory: '',
    genres: [],
    country: '',
    duration: null,
    actors: [],
    imageUrls: [],
    videoUrls: [],
    lastEpisodeTime: '',
    nextEpisodeTime: '',
  });

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...form,
        category: parseInt(form.category, 10),
        year: form.year || 0,
        duration: form.duration || 0,
      };
      const res = await axiosInstance.post('/createArt', payload);
      setSuccess(`Art created with ID: ${res.data.id}`);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <Title order={2} mb="lg">Create Art</Title>
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Title order={4}>Basic Info</Title>
              <TextInput label="Name *" required value={form.name} onChange={e => setField('name', e.target.value)} />
              <Textarea label="Description" autosize minRows={2} value={form.description} onChange={e => setField('description', e.target.value)} />
              <TextInput label="RU Name" value={form.ruName} onChange={e => setField('ruName', e.target.value)} />
              <Textarea label="RU Description" autosize minRows={2} value={form.ruDescription} onChange={e => setField('ruDescription', e.target.value)} />
              <Select label="Category" data={categoryOptions} value={form.category} onChange={v => setField('category', v)} />
              <Select label="Sub Category" data={subCategoryOptions} value={form.subCategory} onChange={v => setField('subCategory', v || '')} clearable />
              <NumberInput label="Year" value={form.year} onChange={v => setField('year', v)} />
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Title order={4}>Links</Title>
              <TextInput label="Wiki Article Link *" required value={form.wikiArticleLink} onChange={e => setField('wikiArticleLink', e.target.value)} />
              <TextInput label="Wiki Article ID" value={form.wikiArticleID} onChange={e => setField('wikiArticleID', e.target.value)} />
              <TextInput label="RU Wiki Article Link" value={form.ruWikiArticleLink} onChange={e => setField('ruWikiArticleLink', e.target.value)} />
              <TextInput label="Thumbnail URL" value={form.thumbnailUrl} onChange={e => setField('thumbnailUrl', e.target.value)} />
              <TagsInput label="Other Platform Links" value={form.otherPlatformsLinks} onChange={v => setField('otherPlatformsLinks', v)} placeholder="Enter URL and press Enter" />
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Title order={4}>Details</Title>
              <TextInput label="Author / Developer" value={form.author} onChange={e => setField('author', e.target.value)} />
              <TextInput label="Publisher" value={form.publisher} onChange={e => setField('publisher', e.target.value)} />
              <TextInput label="Country" value={form.country} onChange={e => setField('country', e.target.value)} />
              <NumberInput label="Duration (minutes)" value={form.duration} onChange={v => setField('duration', v)} />
              <TagsInput label="Genres" value={form.genres} onChange={v => setField('genres', v)} placeholder="Enter genre and press Enter" />
              <TagsInput label="Actors" value={form.actors} onChange={v => setField('actors', v)} placeholder="Enter actor and press Enter" />
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Title order={4}>Media</Title>
              <TagsInput label="Image URLs" value={form.imageUrls} onChange={v => setField('imageUrls', v)} placeholder="Enter URL and press Enter" />
              <TagsInput label="Video URLs" value={form.videoUrls} onChange={v => setField('videoUrls', v)} placeholder="Enter URL and press Enter" />
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Title order={4}>Episode Times (Series)</Title>
              <TextInput label="Last Episode Time (RFC3339)" placeholder="2025-01-15T00:00:00Z" value={form.lastEpisodeTime} onChange={e => setField('lastEpisodeTime', e.target.value)} />
              <TextInput label="Next Episode Time (RFC3339)" placeholder="2025-01-22T00:00:00Z" value={form.nextEpisodeTime} onChange={e => setField('nextEpisodeTime', e.target.value)} />
            </Stack>
          </Paper>

          {error && <Text c="red">{typeof error === 'string' ? error : JSON.stringify(error)}</Text>}
          {success && <Text c="green">{success}</Text>}

          <Button type="submit" loading={loading} size="md">Create Art</Button>
        </Stack>
      </form>
    </div>
  );
};

export default CreateArt;
